import { Badge, Button, EmptyStateHelp, HelpTooltip, LearnMoreLink } from '@auctisync/ui';
import { dollarsToCents, formatCents, type Tables } from '@auctisync/shared';
import { useEffect, useState, type ReactNode } from 'react';
import { readClaims, useSession } from '../../hooks/useSession';
import { supabase } from '../../lib/supabase';

type ItemCategory = Tables<'item_categories'>;
type AuctionCategory = Tables<'auction_categories'>;
type BidScale = Tables<'bid_increment_scales'>;
type TenantOption = Pick<Tables<'tenants'>, 'id' | 'slug' | 'name'>;

type Tier = { min_cents: number; increment_cents: number };

function slugify(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

/**
 * Tenant taxonomy admin. Three panels, one page:
 *   - Item categories      — tag individual inventory items
 *   - Auction categories   — tag entire auctions
 *   - Bid increment scales — named tiered scales for bidding
 *
 * All three are tenant-scoped. Super admins without a primary tenant see
 * a tenant picker at the top to pick which tenant's taxonomies to manage.
 */
export function AdminTaxonomies() {
  const { session } = useSession();
  const claims = readClaims(session);

  const [tenantOptions, setTenantOptions] = useState<TenantOption[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');
  const needsTenantPicker = claims.isSuperAdmin && !claims.tenantId;
  // Derive the effective tenant from claims LIVE each render — the auth session
  // is null on the first render, so a mount-time useState seed froze tenantId at
  // '' and never recovered once the claim loaded (the "Pick a tenant" stranding
  // bug). Only no-primary-tenant super admins fall back to the dropdown.
  const tenantId = needsTenantPicker ? selectedTenantId : (claims.tenantId ?? '');

  useEffect(() => {
    if (!needsTenantPicker) return;
    void (async () => {
      const { data } = await supabase
        .from('tenants')
        .select('id, slug, name')
        .eq('status', 'active')
        .order('name', { ascending: true });
      const list = data ?? [];
      setTenantOptions(list);
      const first = list[0];
      if (first && !selectedTenantId) setSelectedTenantId(first.id);
    })();
  }, [needsTenantPicker, selectedTenantId]);

  return (
    <div>
      <header style={{ marginBottom: '1.5rem' }}>
        <h1
          style={{
            fontFamily: 'var(--font-heading-active)',
            fontSize: '1.875rem',
            fontWeight: 700,
            color: 'var(--text)',
            marginBottom: '0.25rem',
          }}
        >
          Taxonomies
        </h1>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem', marginBottom: '0.35rem' }}>
          Item categories, auction categories, and named bid increment scales.
        </p>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <LearnMoreLink helpSlug="item-categories" newTab>
            About categories
          </LearnMoreLink>
          <LearnMoreLink helpSlug="bid-increment-scales" newTab>
            About bid scales
          </LearnMoreLink>
        </div>
      </header>

      {needsTenantPicker && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            padding: '0.75rem 1rem',
            marginBottom: '1rem',
            borderRadius: 'var(--r-tile, 0.375rem)',
            background: 'var(--surface-muted, #fef3c7)',
            border: '1px solid var(--admin-hairline, var(--border))',
            fontSize: '0.875rem',
            flexWrap: 'wrap',
          }}
        >
          <Badge variant="outline">Super admin</Badge>
          <span>Managing taxonomies for tenant:</span>
          <select
            value={selectedTenantId}
            onChange={(e) => setSelectedTenantId(e.target.value)}
            style={{
              padding: '0.35rem 0.6rem',
              borderRadius: 'var(--r-tile, 0.375rem)',
              border: '1px solid var(--admin-hairline, var(--border))',
              background: 'var(--surface)',
              color: 'var(--text)',
              fontSize: '0.875rem',
            }}
          >
            <option value="">— Pick a tenant —</option>
            {tenantOptions.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.slug})
              </option>
            ))}
          </select>
        </div>
      )}

      {!tenantId ? (
        <EmptyStateHelp
          title="Pick a tenant"
          description="Taxonomies are tenant-scoped. Pick a tenant above to manage their categories and scales."
          helpSlug="item-categories"
        />
      ) : (
        <div style={{ display: 'grid', gap: '1.5rem' }}>
          <ItemCategoriesPanel tenantId={tenantId} />
          <AuctionCategoriesPanel tenantId={tenantId} />
          <BidScalesPanel tenantId={tenantId} />
        </div>
      )}
    </div>
  );
}

// ─── Item categories ─────────────────────────────────────────────────────────

function ItemCategoriesPanel({ tenantId }: { tenantId: string }) {
  const [rows, setRows] = useState<ItemCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const { data, error: err } = await supabase
        .from('item_categories')
        .select('*')
        .eq('tenant_id', tenantId)
        .is('archived_at', null)
        .order('sort_order', { ascending: true })
        .order('name', { ascending: true });
      if (cancelled) return;
      if (err) setError(err.message);
      else setRows(data ?? []);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  async function handleAdd() {
    if (!newName.trim()) return;
    setError(null);
    const { data, error: err } = await supabase
      .from('item_categories')
      .insert({
        tenant_id: tenantId,
        name: newName.trim(),
        slug: slugify(newName),
        sort_order: rows.length * 10,
      })
      .select('*')
      .single();
    if (err) {
      setError(err.message);
      return;
    }
    setRows((prev) => [...prev, data]);
    setNewName('');
    setAdding(false);
  }

  async function handleSaveEdit(id: string) {
    if (!editName.trim()) return;
    const { error: err } = await supabase
      .from('item_categories')
      .update({ name: editName.trim(), slug: slugify(editName) })
      .eq('id', id);
    if (err) {
      setError(err.message);
      return;
    }
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, name: editName.trim(), slug: slugify(editName) } : r)),
    );
    setEditingId(null);
  }

  async function handleArchive(id: string) {
    if (!window.confirm('Archive this category? Items linked to it keep the link.')) return;
    const { error: err } = await supabase
      .from('item_categories')
      .update({ archived_at: new Date().toISOString() })
      .eq('id', id);
    if (err) {
      setError(err.message);
      return;
    }
    setRows((prev) => prev.filter((r) => r.id !== id));
  }

  return (
    <Panel
      title="Item categories"
      subtitle="Tag individual inventory items. Shown in bidder filters."
      helpSlug="item-categories"
      onAddClick={() => setAdding(true)}
    >
      {error && <InlineError>{error}</InlineError>}
      {adding && (
        <AddRow
          placeholder="e.g., Power Tools"
          value={newName}
          onChange={setNewName}
          onSave={() => {
            void handleAdd();
          }}
          onCancel={() => {
            setAdding(false);
            setNewName('');
          }}
        />
      )}
      {loading ? (
        <SkeletonList />
      ) : rows.length === 0 && !adding ? (
        <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
          No categories yet. Click <strong>+ Add</strong> to create your first.
        </span>
      ) : (
        <ul style={listStyle}>
          {rows.map((row) => (
            <RowView
              key={row.id}
              isEditing={editingId === row.id}
              name={row.name}
              editValue={editName}
              onEditChange={setEditName}
              onEditStart={() => {
                setEditingId(row.id);
                setEditName(row.name);
              }}
              onEditCancel={() => setEditingId(null)}
              onEditSave={() => {
                void handleSaveEdit(row.id);
              }}
              onArchive={() => {
                void handleArchive(row.id);
              }}
              secondary={<code style={codeStyle}>{row.slug}</code>}
            />
          ))}
        </ul>
      )}
    </Panel>
  );
}

// ─── Auction categories ──────────────────────────────────────────────────────

function AuctionCategoriesPanel({ tenantId }: { tenantId: string }) {
  const [rows, setRows] = useState<AuctionCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const { data, error: err } = await supabase
        .from('auction_categories')
        .select('*')
        .eq('tenant_id', tenantId)
        .is('archived_at', null)
        .order('sort_order', { ascending: true })
        .order('name', { ascending: true });
      if (cancelled) return;
      if (err) setError(err.message);
      else setRows(data ?? []);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  async function handleAdd() {
    if (!newName.trim()) return;
    const { data, error: err } = await supabase
      .from('auction_categories')
      .insert({
        tenant_id: tenantId,
        name: newName.trim(),
        slug: slugify(newName),
        sort_order: rows.length * 10,
      })
      .select('*')
      .single();
    if (err) {
      setError(err.message);
      return;
    }
    setRows((prev) => [...prev, data]);
    setNewName('');
    setAdding(false);
  }

  async function handleSaveEdit(id: string) {
    if (!editName.trim()) return;
    const { error: err } = await supabase
      .from('auction_categories')
      .update({ name: editName.trim(), slug: slugify(editName) })
      .eq('id', id);
    if (err) {
      setError(err.message);
      return;
    }
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, name: editName.trim(), slug: slugify(editName) } : r)),
    );
    setEditingId(null);
  }

  async function handleArchive(id: string) {
    if (!window.confirm('Archive this auction category?')) return;
    const { error: err } = await supabase
      .from('auction_categories')
      .update({ archived_at: new Date().toISOString() })
      .eq('id', id);
    if (err) {
      setError(err.message);
      return;
    }
    setRows((prev) => prev.filter((r) => r.id !== id));
  }

  return (
    <Panel
      title="Auction categories"
      subtitle={`Tag entire auctions (e.g., "Weekly Tool Auction", "Estate Sale").`}
      helpSlug="item-categories"
      onAddClick={() => setAdding(true)}
    >
      {error && <InlineError>{error}</InlineError>}
      {adding && (
        <AddRow
          placeholder="e.g., Weekly Tool Auction"
          value={newName}
          onChange={setNewName}
          onSave={() => {
            void handleAdd();
          }}
          onCancel={() => {
            setAdding(false);
            setNewName('');
          }}
        />
      )}
      {loading ? (
        <SkeletonList />
      ) : rows.length === 0 && !adding ? (
        <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
          No auction categories yet.
        </span>
      ) : (
        <ul style={listStyle}>
          {rows.map((row) => (
            <RowView
              key={row.id}
              isEditing={editingId === row.id}
              name={row.name}
              editValue={editName}
              onEditChange={setEditName}
              onEditStart={() => {
                setEditingId(row.id);
                setEditName(row.name);
              }}
              onEditCancel={() => setEditingId(null)}
              onEditSave={() => {
                void handleSaveEdit(row.id);
              }}
              onArchive={() => {
                void handleArchive(row.id);
              }}
              secondary={<code style={codeStyle}>{row.slug}</code>}
            />
          ))}
        </ul>
      )}
    </Panel>
  );
}

// ─── Bid increment scales ───────────────────────────────────────────────────

function BidScalesPanel({ tenantId }: { tenantId: string }) {
  const [rows, setRows] = useState<BidScale[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);

  const refresh = async () => {
    const { data, error: err } = await supabase
      .from('bid_increment_scales')
      .select('*')
      .eq('tenant_id', tenantId)
      .is('archived_at', null)
      .order('is_default', { ascending: false })
      .order('name', { ascending: true });
    if (err) setError(err.message);
    else setRows(data ?? []);
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const { data, error: err } = await supabase
        .from('bid_increment_scales')
        .select('*')
        .eq('tenant_id', tenantId)
        .is('archived_at', null)
        .order('is_default', { ascending: false })
        .order('name', { ascending: true });
      if (cancelled) return;
      if (err) setError(err.message);
      else setRows(data ?? []);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  async function handleArchive(id: string) {
    if (!window.confirm('Archive this scale? Active lots keep their link to it.')) return;
    const { error: err } = await supabase
      .from('bid_increment_scales')
      .update({ archived_at: new Date().toISOString() })
      .eq('id', id);
    if (err) {
      setError(err.message);
      return;
    }
    setRows((prev) => prev.filter((r) => r.id !== id));
  }

  return (
    <Panel
      title="Bid increment scales"
      subtitle="Reusable tiered scales attached to auctions and lots."
      helpSlug="bid-increment-scales"
      onAddClick={() => setEditingId('new')}
    >
      {error && <InlineError>{error}</InlineError>}
      {editingId === 'new' && (
        <BidScaleEditor
          tenantId={tenantId}
          scale={null}
          onCancel={() => setEditingId(null)}
          onSaved={async () => {
            await refresh();
            setEditingId(null);
          }}
        />
      )}
      {loading ? (
        <SkeletonList />
      ) : rows.length === 0 && editingId === null ? (
        <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
          No bid scales yet. Every tenant needs at least one default.
        </span>
      ) : (
        <ul style={{ ...listStyle, gap: '0.5rem' }}>
          {rows.map((row) =>
            editingId === row.id ? (
              <li key={row.id}>
                <BidScaleEditor
                  tenantId={tenantId}
                  scale={row}
                  onCancel={() => setEditingId(null)}
                  onSaved={async () => {
                    await refresh();
                    setEditingId(null);
                  }}
                />
              </li>
            ) : (
              <li key={row.id}>
                <BidScaleRow
                  scale={row}
                  onEdit={() => setEditingId(row.id)}
                  onArchive={() => {
                    void handleArchive(row.id);
                  }}
                />
              </li>
            ),
          )}
        </ul>
      )}
    </Panel>
  );
}

function BidScaleRow({
  scale,
  onEdit,
  onArchive,
}: {
  scale: BidScale;
  onEdit: () => void;
  onArchive: () => void;
}) {
  const tiers = scale.tiers as unknown as Tier[];
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0.6rem 0.75rem',
        border: '1px solid var(--admin-hairline, var(--border))',
        borderRadius: 'var(--r-tile, 0.375rem)',
        background: 'var(--surface)',
        gap: '0.75rem',
        flexWrap: 'wrap',
      }}
    >
      <div style={{ flex: '1 1 20rem', minWidth: 0 }}>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <strong style={{ color: 'var(--text)' }}>{scale.name}</strong>
          {scale.is_default && <Badge variant="success">Default</Badge>}
          <code style={codeStyle}>{scale.slug}</code>
        </div>
        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
          {tiers.length} tier{tiers.length === 1 ? '' : 's'} ·{' '}
          {tiers
            .map((t) => `${formatCents(t.min_cents)}+→${formatCents(t.increment_cents)}`)
            .join(' · ')}
        </div>
      </div>
      <div style={{ display: 'flex', gap: '0.35rem' }}>
        <Button type="button" size="sm" variant="outline" onClick={onEdit}>
          Edit
        </Button>
        <Button type="button" size="sm" variant="destructive" onClick={onArchive}>
          Archive
        </Button>
      </div>
    </div>
  );
}

function BidScaleEditor({
  tenantId,
  scale,
  onCancel,
  onSaved,
}: {
  tenantId: string;
  scale: BidScale | null;
  onCancel: () => void;
  onSaved: () => Promise<void> | void;
}) {
  const [name, setName] = useState(scale?.name ?? '');
  const [isDefault, setIsDefault] = useState(scale?.is_default ?? false);
  const [tiers, setTiers] = useState<Tier[]>(() => {
    if (scale) {
      return scale.tiers as unknown as Tier[];
    }
    return [
      { min_cents: 0, increment_cents: 100 },
      { min_cents: 1000, increment_cents: 500 },
      { min_cents: 10000, increment_cents: 2500 },
    ];
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function updateTier(idx: number, field: keyof Tier, dollars: string) {
    const cents = dollars.trim() === '' ? 0 : dollarsToCents(Number(dollars));
    setTiers((prev) => prev.map((t, i) => (i === idx ? { ...t, [field]: cents } : t)));
  }

  function addTier() {
    const last = tiers[tiers.length - 1];
    setTiers((prev) => [
      ...prev,
      { min_cents: (last?.min_cents ?? 0) + 10000, increment_cents: last?.increment_cents ?? 100 },
    ]);
  }

  function removeTier(idx: number) {
    setTiers((prev) => prev.filter((_, i) => i !== idx));
  }

  async function handleSave() {
    setError(null);
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }
    if (tiers.length === 0) {
      setError('At least one tier is required.');
      return;
    }
    const sortedTiers = [...tiers].sort((a, b) => a.min_cents - b.min_cents);
    setSaving(true);
    if (scale) {
      const { error: err } = await supabase
        .from('bid_increment_scales')
        .update({
          name: name.trim(),
          slug: slugify(name),
          tiers: sortedTiers,
          is_default: isDefault,
        })
        .eq('id', scale.id);
      setSaving(false);
      if (err) {
        setError(err.message);
        return;
      }
    } else {
      const { error: err } = await supabase.from('bid_increment_scales').insert({
        tenant_id: tenantId,
        name: name.trim(),
        slug: slugify(name),
        tiers: sortedTiers,
        is_default: isDefault,
      });
      setSaving(false);
      if (err) {
        setError(err.message);
        return;
      }
    }
    await onSaved();
  }

  return (
    <div
      style={{
        padding: '0.75rem',
        border: '1px solid var(--admin-hairline, var(--border))',
        borderRadius: 'var(--r-card, 0.5rem)',
        background: 'var(--surface-muted)',
        display: 'grid',
        gap: '0.75rem',
      }}
    >
      <div style={{ display: 'grid', gap: '0.5rem', gridTemplateColumns: '1fr auto' }}>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Scale name (e.g., DTA Normal Scale)"
          style={inputStyle}
        />
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.35rem',
            fontSize: '0.85rem',
            color: 'var(--text)',
          }}
        >
          <input
            type="checkbox"
            checked={isDefault}
            onChange={(e) => setIsDefault(e.target.checked)}
          />
          Default
          <HelpTooltip helpSlug="bid-increment-scales">
            Only one scale per tenant can be the default.
          </HelpTooltip>
        </label>
      </div>

      <div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr auto',
            gap: '0.5rem',
            marginBottom: '0.35rem',
            fontSize: '0.75rem',
            color: 'var(--text-muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.03em',
          }}
        >
          <span>From</span>
          <span>Increment</span>
          <span />
        </div>
        <div style={{ display: 'grid', gap: '0.35rem' }}>
          {tiers.map((tier, idx) => (
            <div
              key={idx}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr auto',
                gap: '0.5rem',
                alignItems: 'center',
              }}
            >
              <DollarInput
                value={(tier.min_cents / 100).toString()}
                onChange={(v) => updateTier(idx, 'min_cents', v)}
              />
              <DollarInput
                value={(tier.increment_cents / 100).toString()}
                onChange={(v) => updateTier(idx, 'increment_cents', v)}
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => removeTier(idx)}
                disabled={tiers.length === 1}
              >
                Remove
              </Button>
            </div>
          ))}
        </div>
        <div style={{ marginTop: '0.5rem' }}>
          <Button type="button" size="sm" variant="outline" onClick={addTier}>
            + Add tier
          </Button>
        </div>
      </div>

      {error && <InlineError>{error}</InlineError>}

      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
        <Button type="button" size="sm" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={saving}
          onClick={() => {
            void handleSave();
          }}
        >
          {saving ? 'Saving…' : scale ? 'Save changes' : 'Create scale'}
        </Button>
      </div>
    </div>
  );
}

// ─── Shared building blocks ─────────────────────────────────────────────────

function Panel({
  title,
  subtitle,
  helpSlug,
  onAddClick,
  children,
}: {
  title: string;
  subtitle: string;
  helpSlug: string;
  onAddClick: () => void;
  children: ReactNode;
}) {
  return (
    <section
      style={{
        border: '1px solid var(--admin-hairline, var(--border))',
        borderRadius: 'var(--r-card, 0.5rem)',
        background: 'var(--surface-elev)',
        padding: '1rem 1.25rem',
      }}
    >
      <header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '0.75rem',
          marginBottom: '0.75rem',
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h2
            style={{
              fontSize: '1rem',
              fontWeight: 600,
              color: 'var(--text)',
              marginBottom: '0.15rem',
            }}
          >
            {title} <HelpTooltip helpSlug={helpSlug}>Learn about this taxonomy.</HelpTooltip>
          </h2>
          <p style={{ fontSize: '0.825rem', color: 'var(--text-muted)', margin: 0 }}>{subtitle}</p>
        </div>
        <Button type="button" size="sm" variant="outline" onClick={onAddClick}>
          + Add
        </Button>
      </header>
      <div style={{ display: 'grid', gap: '0.5rem' }}>{children}</div>
    </section>
  );
}

function AddRow({
  placeholder,
  value,
  onChange,
  onSave,
  onCancel,
}: {
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        gap: '0.5rem',
        padding: '0.5rem',
        background: 'var(--surface-muted)',
        borderRadius: 'var(--r-tile, 0.375rem)',
        border: '1px dashed var(--border)',
        alignItems: 'center',
      }}
    >
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoFocus
        style={{ ...inputStyle, flex: 1 }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onSave();
          if (e.key === 'Escape') onCancel();
        }}
      />
      <Button type="button" size="sm" onClick={onSave}>
        Save
      </Button>
      <Button type="button" size="sm" variant="outline" onClick={onCancel}>
        Cancel
      </Button>
    </div>
  );
}

function RowView({
  name,
  secondary,
  isEditing,
  editValue,
  onEditChange,
  onEditStart,
  onEditCancel,
  onEditSave,
  onArchive,
}: {
  name: string;
  secondary?: ReactNode;
  isEditing: boolean;
  editValue: string;
  onEditChange: (v: string) => void;
  onEditStart: () => void;
  onEditCancel: () => void;
  onEditSave: () => void;
  onArchive: () => void;
}) {
  if (isEditing) {
    return (
      <li>
        <AddRow
          placeholder=""
          value={editValue}
          onChange={onEditChange}
          onSave={onEditSave}
          onCancel={onEditCancel}
        />
      </li>
    );
  }
  return (
    <li
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0.5rem 0.75rem',
        border: '1px solid var(--admin-hairline, var(--border))',
        borderRadius: 'var(--r-tile, 0.375rem)',
        background: 'var(--surface)',
        gap: '0.5rem',
        flexWrap: 'wrap',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
        <span style={{ color: 'var(--text)' }}>{name}</span>
        {secondary}
      </div>
      <div style={{ display: 'flex', gap: '0.35rem' }}>
        <Button type="button" size="sm" variant="outline" onClick={onEditStart}>
          Edit
        </Button>
        <Button type="button" size="sm" variant="destructive" onClick={onArchive}>
          Archive
        </Button>
      </div>
    </li>
  );
}

function SkeletonList() {
  return (
    <ul style={listStyle}>
      {[0, 1, 2].map((i) => (
        <li
          key={i}
          style={{
            height: '2.25rem',
            borderRadius: 'var(--r-tile, 0.375rem)',
            background: 'var(--surface-muted)',
          }}
        />
      ))}
    </ul>
  );
}

function InlineError({ children }: { children: ReactNode }) {
  return (
    <div
      role="alert"
      style={{
        padding: '0.5rem 0.75rem',
        borderRadius: 'var(--r-tile, 0.375rem)',
        background: 'var(--surface-danger, #fef2f2)',
        color: 'var(--text-danger, #991b1b)',
        border: '1px solid var(--border-danger, #fecaca)',
        fontSize: '0.85rem',
      }}
    >
      {children}
    </div>
  );
}

function DollarInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div style={{ position: 'relative' }}>
      <span
        style={{
          position: 'absolute',
          left: '0.6rem',
          top: '50%',
          transform: 'translateY(-50%)',
          color: 'var(--text-muted)',
          fontSize: '0.875rem',
        }}
      >
        $
      </span>
      <input
        type="text"
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ ...inputStyle, paddingLeft: '1.4rem' }}
        placeholder="0.00"
      />
    </div>
  );
}

const listStyle: React.CSSProperties = {
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'grid',
  gap: '0.35rem',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.45rem 0.65rem',
  border: '1px solid var(--admin-hairline, var(--border))',
  borderRadius: 'var(--r-tile, 0.375rem)',
  background: 'var(--surface)',
  color: 'var(--text)',
  fontSize: '0.875rem',
};

const codeStyle: React.CSSProperties = {
  fontSize: '0.75rem',
  padding: '0.1rem 0.35rem',
  borderRadius: '0.25rem',
  background: 'var(--surface-muted)',
  color: 'var(--text-muted)',
  fontFamily: 'var(--font-mono, monospace)',
};
